import React from 'react'
import { Box, Skeleton } from '@mui/material'

export interface TableSkeletonProps {
  /** Number of skeleton rows to render. Default: 8 */
  rowsCount?: number
  /** Style variant to match page columns layout */
  variant?: 'leads' | 'conversations' | 'campaigns' | 'generic'
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rowsCount = 9,
  variant = 'generic',
}) => {
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        width: '100%',
        height: '100%',
        minHeight: '100%',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: rowsCount }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            px: { xs: 1, sm: 2 },
            borderBottom: index === rowsCount - 1 ? 'none' : '1px solid',
            borderColor: 'divider',
            gap: { xs: 1, sm: 2 },
            flex: 1,
          }}
        >
          {variant === 'leads' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0 }} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1, flexShrink: 0 }} />
              <Skeleton variant="text" width={90} height={20} sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />
              <Skeleton variant="text" width={100} height={20} sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0 }} />
              <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
            </>
          )}

          {variant === 'conversations' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Skeleton variant="circular" width={36} height={36} sx={{ flexShrink: 0 }} />
                <Skeleton variant="text" width="55%" height={20} />
              </Box>
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1, flexShrink: 0 }} />
              <Skeleton variant="text" width={80} height={20} sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />
              <Skeleton variant="text" width={90} height={20} sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0 }} />
            </>
          )}

          {variant === 'campaigns' && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="70%" height={22} />
              </Box>
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1, flexShrink: 0 }} />
              <Skeleton variant="text" width={110} height={20} sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />
              <Skeleton variant="text" width={110} height={20} sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0 }} />
              <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
            </>
          )}

          {variant === 'generic' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0 }} />
                <Skeleton variant="text" width="50%" height={20} />
              </Box>
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 1, flexShrink: 0 }} />
              <Skeleton variant="text" width={90} height={20} sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />
            </>
          )}
        </Box>
      ))}
    </Box>
  )
}

export default TableSkeleton
